import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { toast } from "sonner";
import {
  Download, Mail, FileSpreadsheet, FileText, Database, Loader2, Calendar, Clock,
} from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [emailTo, setEmailTo] = useState(user?.email || "");
  const [busy, setBusy] = useState("");
  const [backups, setBackups] = useState([]);
  const [entities, setEntities] = useState([]);

  const isAdmin = user?.role === "Admin";
  const [settings, setSettings] = useState(null);

  const brandParam = useMemo(
    () => (brands.length === 0 ? "All" : brands.join(",")),
    [brands],
  );
  const brandLabel = brands.length === 0 ? "All" : brands.length === 1 ? brands[0] : `${brands.length} selected`;

  const loadBackups = async () => {
    if (!isAdmin) return;
    try { const r = await api.get("/backups"); setBackups(r.data); } catch {}
  };
  const loadSettings = async () => {
    if (!isAdmin) return;
    try { const r = await api.get("/admin/settings"); setSettings(r.data); } catch {}
  };
  useEffect(() => {
    loadBackups();
    loadSettings();
    api.get("/masters/entities", { params: { only_active: true } })
      .then((r) => setEntities(r.data));
     // eslint-disable-next-line
  }, []);

  const updateFrequency = async (freq) => {
    try {
      const r = await api.put("/admin/settings", { backup_frequency: freq, backup_email: emailTo });
      setSettings(r.data);
      toast.success(`Backup schedule set to ${freq}`);
    } catch { toast.error("Failed"); }
  };

  const downloadExcel = async () => {
    setBusy("excel");
    try {
      const r = await api.get("/reports/positions/excel", { params: { brand: brandParam }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Recruitment_${brandLabel}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  const downloadCsv = async () => {
    setBusy("csv");
    try {
      const r = await api.get("/reports/positions/csv", { params: { brand: brandParam }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Recruitment_${brandLabel}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const r = await api.get("/reports/analytics/pdf", { params: { brand: brandParam }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Analytics_${brandLabel}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  const emailReport = async (kind) => {
    setBusy(`email-${kind}`);
    try {
      await api.post("/reports/email", null, { params: { kind, to_email: emailTo, brand: brandParam } });
      toast.success(`${kind.toUpperCase()} report queued to ${emailTo}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  const manualBackup = async () => {
    setBusy("backup");
    try {
      const r = await api.post("/backups/manual", null, { params: { email: emailTo } });
      toast.success(`Backup created — emailed to ${r.data.emailed_to}`);
      loadBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Operations</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Reports & Backup</h1>
        <p className="text-sm text-muted-foreground mt-1">Generate, download or email recruitment reports.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-4 font-semibold">Filters</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Entity</Label>
              <MultiSelectFilter
                label="Entity"
                value={brands}
                options={entities.map((b) => b.name)}
                onChange={setBrands}
                testId="report-entity"
                width="w-full"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Email To</Label>
              <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="report-email-to" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary grid place-items-center"><Calendar className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="font-medium">Scheduled Backup</div>
              <p className="text-xs text-muted-foreground mt-1">
                Automatic backup runs at 03:00 UTC. Stale-position reminders run daily at 04:00 UTC.
              </p>
            </div>
          </div>
          {isAdmin && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {["daily", "weekly", "monthly", "off"].map((f) => (
                <button
                  key={f}
                  onClick={() => updateFrequency(f)}
                  data-testid={`freq-${f}`}
                  className={`px-3 py-2 rounded-md text-xs font-medium uppercase tracking-widest border transition-colors ${
                    settings?.backup_frequency === f
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted/40 text-foreground/70 border-border hover:bg-muted"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
          {!isAdmin && (
            <div className="text-xs text-muted-foreground">
              Current: <span className="font-medium text-foreground">monthly</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
        <ActionCard
          icon={FileSpreadsheet}
          title="Recruitment Excel"
          desc="All positions exported to Excel."
          action={<Button onClick={downloadExcel} disabled={busy === "excel"} className="w-full" data-testid="report-download-excel">
            {busy === "excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download
          </Button>}
        />
        <ActionCard
          icon={FileSpreadsheet}
          title="CSV Export"
          desc="Plain-text CSV (Excel compatible)."
          action={<Button onClick={downloadCsv} disabled={busy === "csv"} className="w-full" variant="outline" data-testid="report-download-csv">
            {busy === "csv" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download CSV
          </Button>}
        />
        <ActionCard
          icon={FileText}
          title="Analytics PDF"
          desc="KPIs, breakdowns, snapshot."
          action={<Button onClick={downloadPdf} disabled={busy === "pdf"} className="w-full" data-testid="report-download-pdf">
            {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download
          </Button>}
        />
        <ActionCard
          icon={Mail}
          title="Email Report"
          desc="Send Excel / PDF via email."
          action={
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => emailReport("excel")} disabled={busy === "email-excel"} data-testid="report-email-excel">
                Excel
              </Button>
              <Button variant="outline" onClick={() => emailReport("pdf")} disabled={busy === "email-pdf"} data-testid="report-email-pdf">
                PDF
              </Button>
            </div>
          }
        />
        {isAdmin && (
          <ActionCard
            icon={Database}
            title="Manual Backup"
            desc="Excel snapshot saved to cloud + email."
            action={<Button onClick={manualBackup} disabled={busy === "backup"} className="w-full" data-testid="manual-backup-btn">
              {busy === "backup" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Run backup
            </Button>}
          />
        )}
      </div>

      {isAdmin && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
            Backup History
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3">Filename</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono text-xs">{b.filename}</td>
                  <td className="px-4 py-3 text-foreground/80">{b.type}</td>
                  <td className="px-4 py-3 text-foreground/80 tabular-nums">{(b.size / 1024).toFixed(1)} KB</td>
                  <td className="px-4 py-3 text-muted-foreground">{new Date(b.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={`${api.defaults.baseURL}/backups/${b.id}/download?auth=${encodeURIComponent(localStorage.getItem("rms_token"))}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center text-primary hover:underline text-sm"
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                    </a>
                  </td>
                </tr>
              ))}
              {backups.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">
                  <Clock className="w-8 h-8 mx-auto opacity-40 mb-2" />
                  No backups yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, title, desc, action }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 flex flex-col">
      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-medium mb-1">{title}</div>
      <div className="text-xs text-muted-foreground mb-4 flex-1">{desc}</div>
      {action}
    </div>
  );
}
